import 'client/javascript/main';
import WeGlide from 'client/javascript/we_glide/we_glide';
import { Select } from '@wework/ray-core';
import {
  nearbyBuildingsSliderOptions,
  nearbyBuildingsSlider
} from 'client/javascript/pages/home/nearby_buldings/constants';
import { onContentLoaded } from 'client/javascript/on_content_loaded';
import { onPolyfillsLoaded } from 'client/javascript/util/polyfills';
import { trackInteraction, setupScrollAnalyticsTracking } from 'client/javascript/analytics';
import {
  populateNearbyBuildingsSection,
  isNearbyBuildingsAvailable,
  getLocationId
} from 'client/javascript/pages/home/nearby_buldings';
import { setupHeroSlider } from 'client/javascript/pages/home/hero_slider';
import CollapsePanel from 'client/shared/collapse_panel';
import PictureTooltip from 'client/shared/picture_tooltip';
import { fetchGeoData, hasOnDemandBuildingAround, geoData } from 'client/javascript/geo';
import { ON_DEMAND_COUNTRY_ALLOWLIST } from 'shared/util/constants';
import { showElements } from 'client/javascript/util/dom_utils';
import { setupBrowseByWorkspaceModuleSection } from 'client/javascript/pages/shared/sections/browse_by_workspace';
import { showVideoCtaModuleByGeolocation } from 'client/javascript/pages/shared/sections/video_cta';
import { showMediaTextOneAcrossModuleByGeolocation } from 'client/javascript/pages/shared/sections/media_text_one_across';
import { showGeocodedPromoTags } from 'client/javascript/pages/shared/sections/card_product';
import { renderNecessaryModulesBasedOnUserGeolocation } from 'client/javascript/pages/shared/location_based_override_module';
import { setupWizardOverlay } from './wizard/overlay';

const selectorBelowFold = '[data-section-id="home-below-fold"]';
const ALL_ACCESS_CONTAINER = '.all-access-container';
const HERO_TAKEOVER_CONTAINER = '.homepage-promo';
const TRANSPARENT_NAV_CLASS = 'page-nav--transparent';
const TRANSPARENT_NAV_CONTAINER_CLASS = 'page-nav-container--transparent';
const OVERLAYED_HOME_CLASS = 'home-container--overlayed';
const BURGER_WHITE_CLASS = 'burger-color--white';

fetchGeoData();

onContentLoaded(() => {
  onPolyfillsLoaded(async () => {
    const { countryCode } = await geoData;
    setupHeroSlider();
    await populateLocationDropdown();
    showOnDemand();
    await populateNearbyBuildingsSection();

    if (isNearbyBuildingsAvailable()) {
      setupSlider(nearbyBuildingsSlider, nearbyBuildingsSliderOptions);
    }
    CollapsePanel.setUp();
    PictureTooltip.setUp();

    setupWizardOverlay();

    // ANALYTICS
    setupLinkAnalyticsTracking();
    setupScrollAnalyticsTracking({
      label: 'homepage scrolled below the fold',
      selector: selectorBelowFold
    });
    showMediaTextOneAcrossModuleByGeolocation(ALL_ACCESS_CONTAINER);
    setupBrowseByWorkspaceModuleSection();
    showVideoCtaModuleByGeolocation();
    renderHeroTakeoverModule();
    renderNecessaryModulesBasedOnUserGeolocation({
      nodes: document.querySelectorAll(HERO_TAKEOVER_CONTAINER),
      showDefaultModulesIfConditionsExist: false
    });
    showGeocodedPromoTags(countryCode);
  });
});

async function renderHeroTakeoverModule() {
  const takeoverModule = document.querySelector(HERO_TAKEOVER_CONTAINER);
  if (!takeoverModule) {
    return null;
  }

  const { countryCode } = await geoData;
  const { conditionsToShow } = takeoverModule.dataset;
  const isConditionExist = conditionsToShow && Boolean(conditionsToShow.trim().length);
  const listOfCountryCode = isConditionExist ? JSON.parse(conditionsToShow) : [];
  const shouldBeDisplayed = listOfCountryCode.includes(countryCode);

  if (!shouldBeDisplayed) {
    const transparentNav = document.querySelector(`.${TRANSPARENT_NAV_CLASS}`);
    const transparentNavContainer = document.querySelector(`.${TRANSPARENT_NAV_CONTAINER_CLASS}`);
    const overlayedHome = document.querySelector(`.${OVERLAYED_HOME_CLASS}`);
    const mobileMenuBurger = document.querySelector(`.${BURGER_WHITE_CLASS}`);
    if (transparentNav) {
      transparentNav.classList.remove(TRANSPARENT_NAV_CLASS);
    }
    if (transparentNavContainer) {
      transparentNavContainer.classList.remove(TRANSPARENT_NAV_CONTAINER_CLASS);
    }
    if (overlayedHome) {
      overlayedHome.classList.remove(OVERLAYED_HOME_CLASS);
    }
    if (mobileMenuBurger) {
      mobileMenuBurger.classList.remove(BURGER_WHITE_CLASS);
    }
  }
}

async function showOnDemand() {
  const onDemandHomepageModuleSelector = '.video-cta.on-demand-module';
  const doesOnDemandModuleExist = !!document.querySelector(onDemandHomepageModuleSelector);
  if (!doesOnDemandModuleExist) return false;

  const { countryCode } = await geoData;
  if (!ON_DEMAND_COUNTRY_ALLOWLIST.includes(countryCode)) return false;
  const hasOnDemand = await hasOnDemandBuildingAround();
  if (hasOnDemand) {
    showElements(onDemandHomepageModuleSelector);
  }
}

async function populateLocationDropdown() {
  const locationsDropdownSelect = Select.create(document.getElementById('locations-dropdown-select__container'));
  const locationId = await getLocationId();
  locationsDropdownSelect.set(locationId);
}

function setupSlider(selector, options = {}) {
  const glide = new WeGlide(selector, options);
  glide.mount();
}

/* ANALYTICS: clicking anchor tags below the fold */
function setupLinkAnalyticsTracking() {
  const anchorTagsInSectionBelowFold = document.querySelectorAll(`${selectorBelowFold} a`);
  Array.from(anchorTagsInSectionBelowFold).forEach((anchorTag) =>
    anchorTag.addEventListener('click', handleClickAnchorTag)
  );
}

function handleClickAnchorTag(event) {
  const toPath = event.currentTarget.href;
  trackInteraction({ label: 'homepage below the fold content', toPath });
}
